import React from "react";
import { PartyPopper, Radio, Sparkles, Flag, Clock, Hourglass } from "lucide-react";

// Linha simples rótulo: valor
export const InfoLine = ({ label, value, danger }) => {
  if (!value) return null;
  return (
    <p className={`text-sm ${danger ? "font-medium text-[color:var(--brand-danger)]" : "text-[color:var(--brand-graphite)]"}`}>
      <span className="text-[color:var(--brand-muted)]">{label}:</span> {value}
    </p>
  );
};

// Cartão de seção (Banda / AstroLive)
export const SectionCard = ({ title, icon: Icon, children, tone = "default" }) => (
  <div className={`rounded-2xl border p-5 ${tone === "danger" ? "border-[color:var(--brand-danger)] bg-[#FDECEA]" : "border-[color:var(--brand-border)] bg-white"}`}>
    <h4 className={`mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] ${tone === "danger" ? "text-[color:var(--brand-danger)]" : "text-[color:var(--brand-muted)]"}`}>
      {Icon && <Icon className="h-3.5 w-3.5" />} {title}
    </h4>
    {children}
  </div>
);

export const ChipList = ({ items }) => {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return <p className="text-sm text-[color:var(--brand-muted)]">—</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((s) => (
        <span key={s} className="rounded-full bg-[color:var(--brand-champagne-soft)] px-2.5 py-1 text-xs font-medium text-[color:var(--brand-graphite)]">{s}</span>
      ))}
    </div>
  );
};

export const AuthBadge = ({ label, value }) => {
  const yes = value === "sim";
  const answered = value === "sim" || value === "nao";
  return (
    <div className="flex items-center justify-between rounded-xl border border-[color:var(--brand-border)] bg-white px-3 py-2.5">
      <span className="text-sm text-[color:var(--brand-graphite)]">{label}</span>
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${!answered ? "bg-[color:var(--brand-paper)] text-[color:var(--brand-muted)]" : yes ? "bg-[#E7F4EE] text-[color:var(--brand-success)]" : "bg-[#FDECEA] text-[color:var(--brand-danger)]"}`}>
        {!answered ? "A confirmar" : yes ? "Autorizada" : "NÃO autorizada"}
      </span>
    </div>
  );
};

// --- Cronograma visual da festa (linha do tempo) ---
const parseTime = (t) => {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(":").map(Number);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};

const fmtDuration = (mins) => {
  if (mins == null || mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h > 0 ? `${h}h` : ""}${m > 0 ? `${m}min` : h > 0 ? "" : "0min"}`.trim();
};

const TONES = {
  soft: { dot: "bg-[color:var(--brand-champagne)] border-[color:var(--brand-champagne)]", card: "border-[color:var(--brand-border)] bg-white", icon: "text-[color:var(--brand-graphite)]" },
  primary: { dot: "bg-[color:var(--brand-marsala)] border-[color:var(--brand-marsala)]", card: "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)]", icon: "text-[color:var(--brand-marsala)]" },
  info: { dot: "bg-[color:var(--brand-info)] border-[color:var(--brand-info)]", card: "border-[color:var(--brand-border)] bg-[#E9F1FA]", icon: "text-[color:var(--brand-info)]" },
  muted: { dot: "bg-white border-[color:var(--brand-muted)]", card: "border-[color:var(--brand-border)] bg-white", icon: "text-[color:var(--brand-muted)]" },
};

const buildTimelineItems = (f, rel) => {
  const items = [];
  if (f.inicio) items.push({ key: "inicio", time: f.inicio, min: rel(parseTime(f.inicio)), title: "Início da festa", subtitle: "Abertura do evento", icon: PartyPopper, tone: "soft" });
  if (f.outra_atracao && f.outra_atracao_horario)
    items.push({ key: "outra", time: f.outra_atracao_horario, min: rel(parseTime(f.outra_atracao_horario)), title: f.outra_atracao_nome || "Outra atração", subtitle: "Outra atração", icon: Radio, tone: "info" });
  if (!f.show_banda_undefined && f.show_banda)
    items.push({ key: "show_banda", time: f.show_banda, min: rel(parseTime(f.show_banda)), title: "Show — Banda / Receptivo", subtitle: f.show_banda_fim ? `Até ${f.show_banda_fim}` : "Apresentação da banda", icon: Radio, tone: "primary" });
  if (!f.show_astrolive_undefined && f.show_astrolive)
    items.push({ key: "show_astro", time: f.show_astrolive, min: rel(parseTime(f.show_astrolive)), title: "Show — AstroLive", subtitle: f.show_astrolive_fim ? `Até ${f.show_astrolive_fim}` : "Experiência AstroLive", icon: Sparkles, tone: "primary" });
  if (!f.show_banda && !f.show_astrolive && !f.show_banda_undefined && !f.show_astrolive_undefined && !f.show_undefined && f.show)
    items.push({ key: "show", time: f.show, min: rel(parseTime(f.show)), title: "Show LC Produção", subtitle: "Nossa apresentação", icon: Sparkles, tone: "primary" });
  if (f.encerramento) items.push({ key: "fim", time: f.encerramento, min: rel(parseTime(f.encerramento)), title: "Encerramento", subtitle: "Fim previsto do evento", icon: Flag, tone: "muted" });
  return items;
};

export const FestaTimeline = ({ festa }) => {
  const f = festa || {};
  const startMin = parseTime(f.inicio);
  const rel = (min) => (min == null ? null : startMin != null && min < startMin ? min + 1440 : min);

  const items = buildTimelineItems(f, rel);
  const timed = items.filter((i) => i.min != null).sort((a, b) => a.min - b.min);
  const pending = [];
  if (f.show_banda_undefined) pending.push({ key: "show_banda", time: null, title: "Show — Banda / Receptivo", subtitle: "Horário a definir", icon: Radio, tone: "primary" });
  if (f.show_astrolive_undefined) pending.push({ key: "show_astro", time: null, title: "Show — AstroLive", subtitle: "Horário a definir", icon: Sparkles, tone: "primary" });
  if (f.show_undefined && !f.show_banda_undefined && !f.show_astrolive_undefined) pending.push({ key: "show", time: null, title: "Show LC Produção", subtitle: "Horário a definir", icon: Sparkles, tone: "primary" });

  const list = [...timed, ...pending];
  if (list.length === 0) return null;

  const total = startMin != null && f.encerramento && parseTime(f.encerramento) != null ? rel(parseTime(f.encerramento)) - startMin : null;
  const totalLabel = fmtDuration(total);

  return (
    <div data-testid="festa-timeline" className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">
          <Clock className="h-5 w-5" /> Cronograma da festa
        </h3>
        {totalLabel && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-paper)] px-3 py-1 text-xs font-medium text-[color:var(--brand-muted)]">
            <Hourglass className="h-3.5 w-3.5" /> Duração aprox. {totalLabel}
          </span>
        )}
      </div>

      <div className="pl-1">
        {list.map((it, idx) => {
          const Icon = it.icon;
          const tone = TONES[it.tone] || TONES.soft;
          const isLast = idx === list.length - 1;
          return (
            <div key={it.key} data-testid={`festa-timeline-item-${it.key}`} className="flex gap-3 sm:gap-4">
              <div className="w-14 shrink-0 pt-1.5 text-right sm:w-16">
                <span className="font-mono text-sm font-semibold text-[color:var(--brand-graphite)]">{it.time || "--:--"}</span>
              </div>
              <div className="relative flex flex-col items-center">
                <span className={`z-10 mt-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 ${tone.dot}`} />
                {!isLast && <span className="w-px flex-1 bg-[color:var(--brand-border)]" />}
              </div>
              <div className={`mb-3 flex-1 rounded-xl border p-3 ${tone.card}`}>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-graphite)]">
                  <Icon className={`h-4 w-4 ${tone.icon}`} /> {it.title}
                </p>
                <p className="mt-0.5 text-xs text-[color:var(--brand-muted)]">{it.subtitle}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
